import { SxProps, Theme } from "@mui/material";
import colorsConstants from "src/app/constants/colors.constants";

const styles = {
  parent: {
    backgroundColor: colorsConstants.mainContentBackgroundColor,
    flex: 1,
    display: "flex",
    height: "100vh",
  },
  drawerAwareContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "24px",
    overflowY: "auto",
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
  },
  backButton: {
    color: colorsConstants.primaryText,
  },
  selectorContainer: { width: "100%", mb: 2 },
  selectorPaper: {
    p: 2,
    borderRadius: 2,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: colorsConstants.drawerListItemContainerSelected,
    boxShadow: "none",
    maxWidth: 300,
    ml: "auto",
  },
  banner: {
    width: "100%",
    height: { xs: 180, md: 260 },
    objectFit: "cover",
    borderRadius: 2,
    backgroundColor: "rgba(0,0,0,0.06)",
    display: "block",
    mb: 2,
  },
  bannerFallback: {
    width: "100%",
    height: { xs: 180, md: 260 },
    backgroundColor: "rgba(25,118,210,0.08)",
    borderRadius: 2,
    mb: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(25,118,210,0.4)",
    fontSize: 88,
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 1.5,
    mb: 1,
  },
  titleText: {
    color: colorsConstants.primaryText,
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
    flex: 1,
    minWidth: 0,
  },
  chipsRow: {
    display: "flex",
    gap: 0.5,
    flexWrap: "wrap",
    mb: 1.5,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 3,
    mb: 2,
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  metaIcon: {
    color: colorsConstants.primaryText,
    opacity: 0.6,
  },
  metaText: {
    color: colorsConstants.primaryText,
    fontSize: 14,
  },
  metaSecondary: {
    color: colorsConstants.primaryText,
    fontSize: 12,
    opacity: 0.6,
  },
  description: {
    color: colorsConstants.primaryText,
    fontSize: 15,
    lineHeight: 1.6,
    mb: 3,
    whiteSpace: "pre-wrap",
  },
  sectionTitle: {
    color: colorsConstants.primaryText,
    fontWeight: 700,
    mb: 1.5,
    mt: 1,
    fontSize: 18,
  },
  actionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    mb: 3,
  },
  participantList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    mb: 3,
  },
  participantChip: {
    borderRadius: 99,
  },
  mediaIntro: {
    color: colorsConstants.primaryText,
    opacity: 0.75,
    fontSize: 14,
    mb: 2,
  },
  uploadBox: {
    width: "100%",
    minHeight: 120,
    borderRadius: 2,
    border: "2px dashed rgba(0, 0, 0, 0.15)",
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.5)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    p: 2,
    mb: 2,
  },
  uploadIcon: { fontSize: 40, opacity: 0.5, mb: 1 },
  uploadHint: { opacity: 0.7, fontSize: 14, textAlign: "center" as const },
  uploadingPanel: {
    position: "fixed" as const,
    bottom: 24,
    right: 24,
    width: 300,
    zIndex: 1300,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  uploadingCard: {
    p: 1.5,
    borderRadius: 2,
    border: "1px solid rgba(0,0,0,0.1)",
    backgroundColor: colorsConstants.drawerListItemContainerSelected,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
  mediaGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, 1fr)",
      sm: "repeat(3, 1fr)",
      md: "repeat(4, 1fr)",
      lg: "repeat(5, 1fr)",
    },
    gap: 1.5,
    mb: 3,
  },
  mediaTile: {
    position: "relative" as const,
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.06)",
    cursor: "pointer",
    aspectRatio: "1 / 1",
    transition: "transform 0.12s, box-shadow 0.15s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 22px rgba(0,0,0,0.15)",
    },
  },
  mediaImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    display: "block",
  },
  mediaPlayBadge: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "rgba(255,255,255,0.9)",
    fontSize: 40,
    pointerEvents: "none" as const,
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))",
  },
  searchingLoader: {
    display: "flex",
    justifyContent: "center",
    py: 4,
  },
  noItemsText: {
    color: colorsConstants.primaryText,
    opacity: 0.7,
  },
  searchBox: {
    mb: 2,
    backgroundColor: "transparent",
  },
  inviteListItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    py: 1,
    px: 1,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.06)",
    backgroundColor: "rgba(0,0,0,0.02)",
    mb: 1,
  },
  accessRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    py: 1,
    px: 1,
    borderRadius: 1,
    mb: 0.5,
    border: "1px solid rgba(0,0,0,0.06)",
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  accessRowName: {
    color: colorsConstants.primaryText,
    fontSize: 14,
    fontWeight: 500,
  },
  contentCard: {
    p: 2,
    borderRadius: 2,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: colorsConstants.drawerListItemContainerSelected,
    boxShadow: "none",
    mb: 3,
  },
  modalActionsContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "space-evenly",
    mb: 1.5,
  },
  modalCancelButton: { color: colorsConstants.error },
} satisfies Record<string, SxProps<Theme>>;

export default styles;
